import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Key, Webhook, Share2, Plus, Trash2, Send, Copy, Download,
  ShieldCheck, Radio, FileJson, Check,
} from "lucide-react";

const ROLES = ["admin", "cro", "ciso", "cpo", "cfo"];

function Section({ icon: Icon, title, desc, children, action }) {
  return (
    <section className="mt-8 rounded-md border border-black/5 bg-white p-6" data-testid={`interop-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 place-items-center rounded-md bg-ek-purple/10 text-ek-purple">
            <Icon size={18} />
          </span>
          <div>
            <h3 className="font-display text-lg font-semibold text-ek-ink">{title}</h3>
            <p className="mt-0.5 max-w-xl text-sm text-ek-ink/65">{desc}</p>
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Reveal({ label, value, onClose }) {
  return (
    <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="interop-reveal">
      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{label} — copy it now, it won&apos;t be shown again</p>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-white px-3 py-2 font-mono text-xs text-ek-ink" data-testid="interop-reveal-value">{value}</code>
        <button onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} className="ek-btn-outline !py-2" data-testid="interop-reveal-copy"><Copy size={14} /></button>
        <button onClick={onClose} className="ek-btn-outline !py-2" data-testid="interop-reveal-close"><Check size={14} /></button>
      </div>
    </div>
  );
}

export default function Interop() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [info, setInfo] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [keyName, setKeyName] = useState("");
  const [keyRole, setKeyRole] = useState("ciso");
  const [hookUrl, setHookUrl] = useState("");
  const [hookDesc, setHookDesc] = useState("");
  const [deliveries, setDeliveries] = useState({});

  const load = useCallback(() => {
    api.get("/interop/info").then(({ data }) => setInfo(data)).catch(() => {});
    if (isAdmin) {
      api.get("/interop/api-keys").then(({ data }) => setKeys(data.items)).catch(() => {});
      api.get("/interop/webhooks").then(({ data }) => setHooks(data.items)).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    if (!keyName.trim()) return toast.error("Name the key");
    try {
      const { data } = await api.post("/interop/api-keys", { name: keyName, role: keyRole });
      setReveal({ label: "API key", value: data.api_key });
      setKeyName("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const revokeKey = async (id) => {
    try { await api.delete(`/interop/api-keys/${id}`); toast.success("Revoked"); load(); }
    catch { toast.error("Failed"); }
  };

  const createHook = async () => {
    if (!hookUrl.trim()) return toast.error("Enter a URL");
    try {
      const { data } = await api.post("/interop/webhooks", { url: hookUrl, description: hookDesc, events: ["*"] });
      setReveal({ label: "Signing secret", value: data.secret });
      setHookUrl(""); setHookDesc("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteHook = async (id) => {
    try { await api.delete(`/interop/webhooks/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const testHook = async (id) => {
    try {
      const { data } = await api.post(`/interop/webhooks/${id}/test`);
      toast[data.ok ? "success" : "error"](data.ok ? `Delivered (HTTP ${data.status_code})` : `Failed (${data.status_code ?? data.error})`);
      loadDeliveries(id);
    } catch { toast.error("Test failed"); }
  };

  const loadDeliveries = async (id) => {
    try {
      const { data } = await api.get(`/interop/webhooks/${id}/deliveries`);
      setDeliveries((d) => ({ ...d, [id]: data.items }));
    } catch { /* ignore */ }
  };

  const downloadStix = async () => {
    try {
      const { data } = await api.get("/interop/stix/bundle");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "erio-stix-bundle.json";
      a.click();
      toast.success(`Exported ${data.objects.length - 1} indicators`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Interop Fabric" description="API keys, webhooks and STIX/TAXII threat-intel export." path="/interop" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="interop-page">
        <h1 className="ek-eyebrow">Interoperability</h1>
        <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">Interop Fabric</h2>
        <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
          Vendor-neutral integration layer — programmatic API keys, HMAC-signed webhooks and standards-based
          STIX&nbsp;2.1 / TAXII&nbsp;2.1 threat-intel export. ServiceNow & SSO connectors land once a tenant is provisioned.
        </p>

        {reveal && <Reveal {...reveal} onClose={() => setReveal(null)} />}

        {/* Threat-intel export — visible to all roles */}
        <Section icon={Share2} title="Threat intelligence export"
          desc="Export risk signals as a STIX 2.1 bundle, or pull them over TAXII 2.1."
          action={<button onClick={downloadStix} className="ek-btn flex items-center gap-1.5" data-testid="interop-stix-download"><Download size={15} /> STIX bundle</button>}>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <a href={`${api.defaults.baseURL}/interop/openapi.json`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-md border border-black/5 bg-ek-canvas p-3 text-sm text-ek-ink hover:border-ek-purple/40" data-testid="interop-openapi-link">
              <FileJson size={16} className="text-ek-purple" /> OpenAPI 3.1 schema
            </a>
            <div className="flex items-center gap-2 rounded-md border border-black/5 bg-ek-canvas p-3 text-sm text-ek-ink">
              <Radio size={16} className="text-ek-purple" /> TAXII root <code className="ml-auto font-mono text-xs text-ek-ink/65">/api/taxii2/</code>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-black/5 bg-ek-canvas p-3 text-sm text-ek-ink">
              <ShieldCheck size={16} className="text-ek-purple" /> {info?.events?.length ?? 0} event types
            </div>
          </div>
        </Section>

        {!isAdmin && (
          <p className="mt-8 rounded-md border border-black/5 bg-white p-5 text-sm text-ek-ink/65" data-testid="interop-admin-note">
            API-key and webhook management is restricted to administrators.
          </p>
        )}

        {isAdmin && (
          <>
            {/* API keys */}
            <Section icon={Key} title="API keys" desc="Machine-to-machine credentials, scoped by RBAC role. Shown once on creation.">
              <div className="mt-4 flex flex-wrap items-end gap-2">
                <input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Key name (e.g. SIEM connector)" data-testid="apikey-name-input" className="flex-1 rounded-md border border-black/10 px-3 py-2 text-sm" />
                <select value={keyRole} onChange={(e) => setKeyRole(e.target.value)} data-testid="apikey-role-select" className="rounded-md border border-black/10 px-3 py-2 text-sm">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button onClick={createKey} className="ek-btn flex items-center gap-1.5" data-testid="apikey-create-button"><Plus size={15} /> Generate</button>
              </div>
              <div className="mt-4 space-y-2" data-testid="apikey-list">
                {keys.length === 0 ? <p className="text-sm text-ek-ink/65">No API keys yet.</p> :
                  keys.map((k) => (
                    <div key={k.key_id} className="flex flex-wrap items-center gap-3 rounded-md border border-black/5 bg-ek-canvas p-3" data-testid={`apikey-${k.key_id}`}>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${k.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>{k.active ? "active" : "revoked"}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-ek-ink">{k.name} <span className="text-ek-ink/65">· {k.role}</span></p>
                        <p className="font-mono text-xs text-ek-ink/65">{k.prefix}… {k.last_used_at ? `· last used ${k.last_used_at.slice(0, 10)}` : "· never used"}</p>
                      </div>
                      {k.active && <button onClick={() => revokeKey(k.key_id)} className="ek-btn-outline !py-1.5 text-xs" data-testid={`apikey-revoke-${k.key_id}`}><Trash2 size={13} /> Revoke</button>}
                    </div>
                  ))}
              </div>
            </Section>

            {/* Webhooks */}
            <Section icon={Webhook} title="Webhooks" desc="HMAC-SHA256 signed delivery of domain events to any endpoint.">
              <div className="mt-4 flex flex-wrap items-end gap-2">
                <input value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://your-system/erio-webhook" data-testid="webhook-url-input" className="flex-1 rounded-md border border-black/10 px-3 py-2 text-sm" />
                <input value={hookDesc} onChange={(e) => setHookDesc(e.target.value)} placeholder="Description (optional)" data-testid="webhook-desc-input" className="rounded-md border border-black/10 px-3 py-2 text-sm" />
                <button onClick={createHook} className="ek-btn flex items-center gap-1.5" data-testid="webhook-create-button"><Plus size={15} /> Subscribe</button>
              </div>
              <div className="mt-4 space-y-2" data-testid="webhook-list">
                {hooks.length === 0 ? <p className="text-sm text-ek-ink/65">No webhooks yet.</p> :
                  hooks.map((h) => (
                    <div key={h.webhook_id} className="rounded-md border border-black/5 bg-ek-canvas p-3" data-testid={`webhook-${h.webhook_id}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${h.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>{h.active ? "active" : "off"}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-ek-ink">{h.url}</p>
                          <p className="text-xs text-ek-ink/65">{h.description || "—"} · {Array.isArray(h.events) ? h.events.join(", ") : h.events}{h.last_ok != null ? ` · last ${h.last_ok ? "ok" : "failed"}` : ""}</p>
                        </div>
                        <button onClick={() => testHook(h.webhook_id)} className="ek-btn-outline !py-1.5 text-xs" data-testid={`webhook-test-${h.webhook_id}`}><Send size={13} /> Test</button>
                        <button onClick={() => loadDeliveries(h.webhook_id)} className="ek-btn-outline !py-1.5 text-xs" data-testid={`webhook-deliveries-${h.webhook_id}`}>Log</button>
                        <button onClick={() => deleteHook(h.webhook_id)} className="ek-btn-outline !py-1.5 text-xs" data-testid={`webhook-delete-${h.webhook_id}`}><Trash2 size={13} /></button>
                      </div>
                      {deliveries[h.webhook_id]?.length > 0 && (
                        <div className="mt-2 space-y-1 border-t border-black/5 pt-2" data-testid={`webhook-delivery-log-${h.webhook_id}`}>
                          {deliveries[h.webhook_id].slice(0, 5).map((d) => (
                            <div key={d.delivery_id} className="flex items-center gap-2 text-xs text-ek-ink/65">
                              <span className={d.ok ? "text-emerald-600" : "text-red-500"}>{d.ok ? "✓" : "✕"}</span>
                              <span className="font-mono">{d.event}</span>
                              <span>HTTP {d.status_code ?? "—"}</span>
                              <span className="ml-auto">{d.attempted_at?.slice(11, 19)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </Section>
          </>
        )}
      </main>
    </div>
  );
}
